<template>
  <Stack class="stk-py-8">
    <div class="row">
      <!-- Generate AdvisorCore Report -->
      <div class="row-body">
        <Title size="5">
          Generate Reports:
        </Title>
        <StakeTooltip
          v-if="!entity.shareownership_or_uploaded_df_agg_exists"
          :label="`${entity.name} needs a portfolio to run this report. You can add holdings on the Portfolio tab`"
          position="top"
          z-index="1"
        >
          <span>
            <Button
              :to="{ name: 'auto-portfolio', query: { portfolio: entityExternalId, person_entity_id: entityExternalId } }"
              variant="primary"
              target="_blank"
              rel="noreferrer noopener"
              :disabled="!entity.shareownership_or_uploaded_df_agg_exists"
            >
              Launch Portfolio Intelligence Report
            </Button>
            <Button
              :to="entity.shareownership_or_uploaded_df_agg_exists ? { path: `/ui/advisor/reports/`, query: { reporting: entityExternalId } } : null"
              variant="primary"
              target="_blank"
              rel="noreferrer noopener"
              :disabled="!entity.shareownership_or_uploaded_df_agg_exists"
              >
                Launch Impact Report
            </Button>
          </span>
        </StakeTooltip>
        <template v-else>
          <Button
            :to="{ name: 'auto-portfolio', query: { portfolio: entityExternalId, person_entity_id: entityExternalId } }"
            variant="primary"
            target="_blank"
            rel="noreferrer noopener"
          >
            Launch Portfolio Intelligence Report
          </Button>
          <Button
            :href="`/ui/advisor/reports/?reporting=${entityExternalId}`"
            variant="primary"
            target="_blank"
            rel="noreferrer noopener"
            class="tw-ml-2"
          >
            Launch Impact Report
          </Button>
        </template>
      </div>
    </div>

    <div class="row">
      <p v-if="saveRiskMessage" class="notification is-success mb-0">
        {{ saveRiskMessage }}
      </p>
      
      <div class="row-body">
        <Title size="5">
          Client Risk Score:
        </Title>

        <Badge status="primary" v-if="manualRisk && riskQuestionnaireScore === null">
          {{ manualRisk }}
        </Badge>
        <Badge status="primary" v-if="riskQuestionnaireScore && manualRisk === null">
          {{ riskQuestionnaireScore }} (From Risk Questionnaire)
        </Badge>
        <Badge status="primary" v-if="riskQuestionnaireScore && manualRisk">
          {{ manualRisk }} (Set Manually)
          {{ riskQuestionnaireScore }} (From Risk Questionnaire)
        </Badge>
        <Badge status="warning" v-if="!manualRisk && !riskQuestionnaireScore">
          Not selected
        </Badge>

        <Button
          v-if="canEdit"
          @click="openManualRiskEditor"
          icon-before="fas fa-edit"
          size="3"
        >
          Edit
        </Button>
      </div>

      <div v-if="editingManualRisk" class="row-form -risk-editor">
        <div class="field">
          <label class="label mb-0">Manual Risk Score</label>
          <p class="help">
            Enter a whole number between 0 and 100.
          </p>
          <div class="control stk-mt-2">
            <input
              class="stk-input tw-w-full"
              type="number"
              min="0"
              max="100"
              step="1"
              v-model.number="editedManualRisk"
              @keyup.enter="saveManualRisk"
            />
          </div>
        </div>
        <Buttons>
          <Button
            variant="primary"
            :disabled="!isManualRiskValid"
            @click="saveManualRisk"
          >
            Save
          </Button>
          <Button @click="cancelManualRiskEdit">Cancel</Button>
          <span class="tw-ml-4" v-if="saveRiskMessage">{{ saveRiskMessage }}</span>
        </Buttons>
      </div>
    </div>

    <div class="row">
      <p v-if="valuesMessage" class="notification is-success mb-0">
        {{ valuesMessage }}
      </p>

      <div class="row-body">
        <Title size="5">
          Client Values:
        </Title>

        <Badge status="warning" v-if="clientValues.length === 0">
          Not selected
        </Badge>

        <Badges v-else>
          <Dropdown
            v-for="set in screenSetsWithMetrics"
            anchor="left"
          >
            <template #trigger="{ toggle }">
              <Badge status="primary" @click="toggle">
                <span class="tw-inline-flex tw-items-center tw-gap-1">
                  {{ set.name }}
                  <i class="fas fa-chevron-down" />
                </span>
              </Badge>
            </template>
            <div class="tw-max-h-64 tw-overflow-auto tw-w-64 tw-px-2 tw-py-2">
              <div
                v-if="!set.metrics || set.metrics.filter(m => m && m.details).length === 0"
                class="tw-text-gray-500 tw-text-sm"
              >
                No metrics
              </div>
              <div v-else class="tw-space-y-1">
                <div
                  v-for="metric in set.metrics"
                  v-if="metric && metric.details"
                >
                  <span
                    :class="[
                      'tag',
                      'is-light',
                      'metric-tag',
                      'is-' + ((metric.details && metric.details.subcategory && metric.details.subcategory[0] && metric.details.subcategory[0].esgissue && metric.details.subcategory[0].esgissue.slug) ? metric.details.subcategory[0].esgissue.slug : 'primary')
                    ]"
                  >
                    {{ metric.details.name }}
                  </span>
                </div>
              </div>
            </div>
          </Dropdown>
        </Badges>

        <Button
          v-if="canEdit"
          @click="openValuesPicker(originalValues)"
          icon-before="fas fa-edit"
          size="3"
        >
          Edit
        </Button>
      </div>
    </div>

    <!-- Questionnaire Results Section -->
    <template v-if="hasFetchedQuestionnaires">
      <Stack gap="4">
        <Title>Questionnaire Results</Title>

        <Callout
          v-if="followupLinkDisabled"
          status="warning"
        >
          The client needs to complete the Behavioral Values Questionnaire
          or the Values Picker before being able to access those links.
        </Callout>

        <Buttons>
          <Button
            variant="primary"
            icon-before="fas fa-chart-pie"
            :disabled="followupLinkDisabled"
            :to="{
              name: 'reports',
              query: { values_from: entity?.linked_entity_external_id },
            }"
          >
            View in Impact Reports
          </Button>
          <Button
            variant="primary"
            outlined
            icon-before="fas fa-search-dollar"
            :disabled="followupLinkDisabled"
            :to="{
              name: 'fund-screener',
              query: { values_from: entity?.linked_entity_external_id },
            }"
          >
            View in Fund Screener
          </Button>
          <Button
            variant="primary"
            outlined
            icon-before="fas fa-search"
            :disabled="followupLinkDisabled"
            :href="companyScreenerURL"
          >
            View in Company Screener
          </Button>
        </Buttons>
      </Stack>

      <div v-if="questionnaireResults.length > 0">
        <o-table
          :data="questionnaireResults"
          sortable
          hoverable
        >
          <o-table-column field="name" label="Questionnaire Type" sortable>
            <template v-slot="props">
              {{ props.row.name }}
            </template>
          </o-table-column>
          <o-table-column field="date" label="Date" sortable>
            <template v-slot="props">
              {{ formatDate(props.row.date) }}
            </template>
          </o-table-column>
          <o-table-column field="actions" label="Results URL">
            <template v-slot="props">
              <Button
                variant="primary"
                size="3"
                :href="props.row.url"
                target="_blank"
              >
                View Results
              </Button>
            </template>
          </o-table-column>
        </o-table>
      </div>

      <!-- Send New Questionnaire Section -->
      <Stack gap="4">
        <Title size="5">
          Send New Questionnaire
        </Title>

        <o-table
          :data="questionnaires"
          sortable
          hoverable
        >
          <o-table-column field="name" label="Questionnaire Type" sortable>
            <template v-slot="props">
              {{ props.row.name }}
            </template>
          </o-table-column>
          <o-table-column field="copy" label="Questionnaire URL">
            <template v-slot="props">
              <CopyToClipboard
                :value="props.row.url"
                size="3_5"
              >
                <StakeTooltip label="Open in new tab">
                  <Button
                    :href="props.row.url"
                    target="_blank"
                    resize="3"
                    variant="primary"
                    icon-only="fas fa-external-link-alt"
                  />
                </StakeTooltip>
              </CopyToClipboard>
            </template>
          </o-table-column>
        </o-table>
      </Stack>

      <Stack gap="4">
        <Title>Portfolio Intelligence Past Analyses</Title>
        <PastAdvisorCoreAnalyses :entityExternalId="entityExternalId" hide-header />
      </Stack>
    </template>

    <LoaderWithText v-else>
      Fetching Questionnaires…
    </LoaderWithText>
  </Stack>
</template>

<script>
import EntityTree from './EntityTree.vue';
import Advisor from '@frontend/dash3/js/shared/Advisor.js';
import PastAdvisorCoreAnalyses from '@frontend/ui/features/homepage/views/PastAdvisorCoreAnalyses.vue';
import ValuesPicker from '@frontend/dash3/js/components/ValuesPicker.vue';
import { mapStores } from 'pinia';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import * as Sentry from '@sentry/vue';
import {
  Badge,
  Badges,
  Button,
  Buttons,
  Callout,
  Dropdown,
  Hr,
  CopyToClipboard,
  LoaderWithText,
  Stack,
  StakeTooltip,
  Title,
} from '@stakeui';

export default {
  name: 'EntityReports',
  components: {
    EntityTree,
    Advisor,
    PastAdvisorCoreAnalyses,
    Badge,
    Badges,
    Button,
    Buttons,
    Callout,
  Dropdown,
    Hr,
    CopyToClipboard,
    LoaderWithText,
    Stack,
    StakeTooltip,
    Title,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      treeData: null,
      questionnaires: [],
      questionnaireResults: [],
      screenSetsById: {},
      metricsById: {},
      metricsLoaded: false,
      linkedProspectClientId: null,
      selectedNodes: [],
      hasFetchedQuestionnaires: false,
      hasQuestionnaireResponse: false,
      clientHasRiskQuestionnaireResults: false,
      clientHasWizardScreenSets: false,
      questionnaireDate: null,
      riskQuestionnaireDate: null,
      wizardScreenSetsDate: null,
      entityType: 'Other',
      advisor: null,
      originalValues: [],
      editedValues: [],
      valuesMessage: '',
      editedManualRisk: null,
      editingManualRisk: false,
      manualRiskValue: null,
      saveRiskMessage: '',
    };
  },
  created() {
    this.getLinkedProspectClient();
    this.fetchEntityTree();
    this.advisor = new Advisor();
    this.originalValues.push(...this.entity.screen_sets);
    this.manualRiskValue = this.entity.manual_risk ?? null
    // load metrics and then enrich screen sets with metric details
    this.fetchImpactFactors().then(() => {
      this.fetchScreenSetsDetails();
    });
  },
  computed: {
    ...mapStores(useAdvisorStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    canEdit() {
      return (
        this.entity &&
        this.entity.user_permissions &&
        this.entity.user_permissions.includes('change_entity')
      );
    },
    manualRisk() {
      return this.manualRiskValue
    },
    isManualRiskValid() {
      return (
        Number.isInteger(this.editedManualRisk) &&
        this.editedManualRisk >= 0 &&
        this.editedManualRisk <= 100
      );
    },
    clientValues() {
      return this.entity?.screen_sets?.map(s => s.name) || [];
    },
    screenSetsWithMetrics() {
      const base = this.entity?.screen_sets || [];
      return base.map(s => this.screenSetsById[s.id] || s);
    },
    riskQuestionnaireScore() {
      return this.entity?.risk_questionnaire_score ?? null;
    },
    companyScreenerURL() {
      if (this.followupLinkDisabled) {
        return '';
      }

      const url = new URL('/advisordashboard/portfolio-builder/', window.location.origin);
      url.searchParams.set('values_from', this.entity.linked_entity_external_id);
      return url.toString();
    },
    followupLinkDisabled() {
      return !this.entity?.linked_entity_external_id || (!this.hasQuestionnaireResponse && !this.clientHasWizardScreenSets)
    },
  },
  methods: {
    async fetchImpactFactors() {
      try {
        const resp = await fetch('/api/v1/impact_factors/');
        if (!resp.ok) throw new Error('Failed to fetch metrics');
        const data = await resp.json();
        const map = {};
        (data.metrics || []).forEach(itm => {
          map[itm.id] = itm;
        });
        this.metricsById = map;
        this.metricsLoaded = true;
      } catch (e) {
        this.metricsLoaded = false;
        // non-blocking, UI will show "No metrics" if this fails
        // optionally log
        // console.error(e);
      }
    },
    async fetchScreenSetsDetails() {
      try {
        const ids = (this.entity?.screen_sets || []).map(s => s.id).filter(Boolean);
        if (!ids.length) return;
        const resp = await fetch(`/api/v1/screen_sets/?ids=${ids.join(',')}`);
        if (!resp.ok) throw new Error('Failed to fetch screen sets');
        const data = await resp.json();
        const sets = data.screen_sets || [];
        sets.forEach(set => {
          if (Array.isArray(set.metrics)) {
            set.metrics = set.metrics
              .map(m => {
                const details = this.metricsById[m.metric_description];
                return details ? { ...m, details } : null;
              })
              .filter(Boolean);
          }
          // ensure reactivity when adding new keys
          this.$set(this.screenSetsById, set.id, set);
        });
      } catch (e) {
        // console.error(e);
      }
    },
    formatDate(date) {
      if (!date) return 'N/A'; // Handle cases where the date is null or undefined
      const options = { year: 'numeric', month: 'short', day: 'numeric' }; // Customize format
      return new Date(date).toLocaleDateString(undefined, options);
    },
    async getLinkedProspectClient() {
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/get_linked_prospect_client_portfolio/`);
        if (!response.ok) throw new Error('Failed to fetch linked prospect client');
        const data = await response.json();
        this.entityType = data.entity_type;
        this.linkedProspectClientId = data.external_id;
        this.hasQuestionnaireResponse = data.has_questionnaire_response;
        this.clientHasRiskQuestionnaireResults = data.risk_questionnaire_score;
        this.clientHasWizardScreenSets = data.has_wizard_screen_sets;
        this.questionnaireDate = data.questionnaire_date;
        this.riskQuestionnaireDate = data.risk_questionnaire_date;
        this.wizardScreenSetsDate = data.wizard_screen_sets_date;

        // Populate tables once ID is available
        this.populateQuestionnaires();
        this.populateQuestionnaireResults();
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error fetching results',
          type: 'is-danger',
        });
        Sentry.captureMessage('Error fetching linked prospect client in entity reports', { level: 'warning' });
      } finally {
        this.hasFetchedQuestionnaires = true;
      }
    },
    async fetchEntityTree() {
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/tree/`);
        if (!response.ok) throw new Error('Failed to fetch entity tree');
        this.treeData = await response.json();
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error fetching entity tree',
          type: 'is-danger',
        });
        Sentry.captureMessage('Error fetching entity tree in entity reports', { level: 'warning' });
      }
    },
    populateQuestionnaires() {
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      this.questionnaires = [
        {
          name: 'Risk Questionnaire',
          url: `${baseUrl}/questionnaire/questions/v2_${btoa(this.linkedProspectClientId)}/?questionnaire_type=risk`,
        },
        {
          name: 'Behavioral Values Questionnaire',
          url: `${baseUrl}/questionnaire/questions/v2_${btoa(this.linkedProspectClientId)}/`,
        },
      ];
      this.questionnaires.push({
        name: 'Values Picker',
        url: `${baseUrl}/ui/visitor/wizard?advisor=${this.advisor.link_slug}&prospect=${this.linkedProspectClientId}`,
      });
    },
    populateQuestionnaireResults() {
      const baseUrl = `${window.location.protocol}//${window.location.host}`;
      const results = [];

      if (this.hasQuestionnaireResponse) {
        results.push({
          name: 'Behavioral Values Questionnaire',
          date: this.questionnaireDate,
          url: `${baseUrl}/questionnaire/review-results/${this.linkedProspectClientId}/`,
        });
      }

      if (this.clientHasRiskQuestionnaireResults) {
        results.push({
          name: 'Risk Questionnaire',
          date: this.riskQuestionnaireDate,
          url: `${baseUrl}/ui/advisor/risk/results/${this.linkedProspectClientId}/`,
        });
      }

      if (this.clientHasWizardScreenSets) {
        results.push({
          name: 'Values Picker',
          date: this.wizardScreenSetsDate,
          url: `${baseUrl}/ui/advisor/wizard/results?prospect=${this.linkedProspectClientId}`,
        });
      }

      this.questionnaireResults = results;
    },
    copyToClipboard(link) {
      navigator.clipboard.writeText(link).then(
        () => {
          this.$buefy.toast.open({
            message: `Link Copied to Clipboard`,
            type: 'is-success',
          });
        },
        (err) => {
          console.error('Failed to copy text: ', err);
        }
      );
    },
    handleCheckboxToggle({ node, isChecked }) {
      if (isChecked) {
        this.selectedNodes.push(node);
      } else {
        this.selectedNodes = this.selectedNodes.filter((n) => n.entity.external_id !== node.entity.external_id);
      }
    },
    openValuesPicker: function(currentlyApplied) {
      if (this.originalValues.length === 0) {
        this.originalValues.push(...this.entity.screen_sets);
      }
      console.log(this.entity.screen_sets)
      this.$buefy.modal.open({
        parent: this,
        component: ValuesPicker,
        hasModalCard: true,
        trapFocus: true,
        props: {
          additionalValues: this.originalValues,
          currentlyApplied: this.entity.screen_sets,
          callback: this.valuesChangeCallback,
        },
      });
    },
    openManualRiskEditor() {
      this.editedManualRisk = this.manualRiskValue
      this.editingManualRisk = true
      this.saveRiskMessage = ''
    },
    cancelManualRiskEdit() {
      this.editedManualRisk = null;
      this.editingManualRisk = false;
      this.saveRiskMessage = '';
    },
    async saveManualRisk() {
      if (!this.isManualRiskValid) {
        this.saveRiskMessage = 'Enter an integer between 0 and 100.'
        return
      }
      try {
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`
        const response = await fetch(url, {
          method: 'POST',
          headers: { ...this.headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ manual_risk: this.editedManualRisk })
        })
        if (!response.ok) {
          const err = await response.json()
          throw new Error(err.error || 'Update failed.')
        }

        // ← NEW: only update the local copy
        this.manualRiskValue = this.editedManualRisk

        this.editingManualRisk = false
        this.saveRiskMessage = 'Manual risk score updated successfully!'
        setTimeout(() => (this.saveRiskMessage = ''), 2000)
      } catch (e) {
        console.error('Error updating manual risk:', e)
        this.saveRiskMessage = e.message || 'Failed to update manual risk. Try again.'
      }
    },
    async valuesChangeCallback(newValues) {
      this.editedValues = newValues;
      this.valuesMessage = '';

      try {
        const url = `/api/v1/entities/${this.entityExternalId}/update-entity-field/`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 
            ...this.headers, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify({ screen_sets: newValues })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Update failed.');
        }

        // push the successful change into your prop + originalValues
        this.entity.screen_sets = newValues;
        this.originalValues = [...newValues];

        this.valuesMessage = 'Client values updated successfully!';
        // refresh enriched metrics for updated sets
        if (this.metricsLoaded) {
          this.fetchScreenSetsDetails();
        } else {
          this.fetchImpactFactors().then(() => this.fetchScreenSetsDetails());
        }
        // clear it in a couple seconds
        setTimeout(() => {
          this.valuesMessage = '';
        }, 2000);

      } catch (error) {
        console.error('Error updating client values:', error);
        this.valuesMessage =
          error.message || 'Failed to update client values. Try again.';
      }
    },
  },
};
</script>

<style scoped>
.row {
  display: flex;
  align-items: stretch;
  flex-direction: column;
  justify-content: start;
  box-shadow: var(--stk-controls-shadow);
  background-color: var(--stk-scheme-main);
  gap: 1em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;
  border-radius: 1em;
  border: 1px solid var(--stk-border);
  padding: 1.5em;
}

.row-body {
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 1em;
}

.row-form {
  background-color: var(--stk-background);
  border: 1px solid var(--stk-border);
  border-radius: 1em;
  padding: 1em 1.25em;
}
</style>
